import moment, { Moment } from 'moment';
import React, { ReactNode } from 'react';
import { Box } from '../../../Box';
import { daysInMonth } from '../../Month/util';
import { MomentRanges, RangeIndex } from '../../types';
import { MinimapDay } from '../MinimapDay';
import css from './index.module.scss';

export interface MinimapMonthProps {
  /**
   * The month that will be rendered, must be a Moment object.
   */
  month: Moment;
  /**
   * The selected/comparison date ranges. The `MinimapDay` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Controls the which range takes priority when rendering overlapping ranges.
   */
  focusedRange: RangeIndex;
  /**
   * The minimum selectable date. If provided, days before this date will be disabled.
   */
  minDate?: Moment;
  /**
   * The maximum selectable date. If provided, days after this date will be disabled.
   */
  maxDate?: Moment;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Moment) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Moment) => ReactNode;
}

const MinimapMonth: React.FC<MinimapMonthProps> = ({
  month,
  ranges,
  focusedRange,
  minDate,
  maxDate,
  isDayDisabled,
  renderInfoTooltip,
}) => {
  const dayOffset = moment(month).startOf('month').day();
  return (
    <Box data-component="MinimapMonth" className={css.dayGrid}>
      {dayOffset > 0 && <Box style={{ gridColumnStart: dayOffset }} />}
      {daysInMonth(month).map(day => {
        const isOutsideMinMax =
          (minDate && day.isBefore(minDate, 'day')) || (maxDate && day.isAfter(maxDate, 'day'));
        return (
          <MinimapDay
            key={day.toISOString()}
            day={day}
            ranges={ranges}
            focusedRange={focusedRange}
            disabled={isOutsideMinMax || isDayDisabled?.(day)}
            infoTooltip={renderInfoTooltip?.(day)}
          />
        );
      })}
    </Box>
  );
};

const MemoizedMinimapMonth = React.memo(MinimapMonth);

export { MemoizedMinimapMonth as MinimapMonth };
